import { ContactUs } from "../components/ContactUs";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { Nav } from "../components/Nav";
import { TemplateArt } from "../components/TemplateArt";
import { Heart } from "lucide-react";

export default function Gallery() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/templates/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/templates", { params: active === "All" ? {} : { category: active } })
      .then(({ data }) => setTemplates(data))
      .finally(() => setLoading(false));
  }, [active]);

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold">Pick a template</h1>
        <p className="text-[#4A4A4A] mt-2">Choose a design, then make it yours. Sending is always free.</p>

        <div className="flex flex-wrap gap-2 mt-6" data-testid="category-filters">
          {categories.map((c, i) => {
            const palette = ["#FFB5A7", "#FFE066", "#A8E6CF", "#C8B6FF", "#FFB5D8", "#8AC6FF", "#FF8C94", "#FFD6A5", "#B5EAD7", "#FFDAC1"];
            const bg = palette[i % palette.length];
            return (
              <button
                key={c}
                data-testid={`category-${c}`}
                onClick={() => setActive(c)}
                style={{ backgroundColor: bg }}
                className={`px-4 py-2 rounded-xl font-bold border-2 border-[#0A0A0A] transition-all hover:-translate-y-0.5 ${
                  active === c
                    ? "shadow-[3px_3px_0px_#0A0A0A] ring-2 ring-[#0A0A0A] ring-offset-2 scale-[1.03]"
                    : "shadow-[2px_2px_0px_#0A0A0A] hover:shadow-[3px_3px_0px_#0A0A0A]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="nb-card h-64 animate-pulse bg-gray-100" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8" data-testid="template-grid">
            {templates.map((t) => (
              <button
                key={t.id}
                data-testid={`template-${t.id}`}
                onClick={() => navigate(`/create/${t.id}`)}
                className="group nb-card p-0 overflow-hidden text-left hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#0A0A0A] transition-all"
              >
                <div className="relative h-52 overflow-hidden border-b-2 border-[#0A0A0A]">
                  {t.image_url ? (
                    <img src={resolveImg(t.image_url)} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full group-hover:scale-105 transition-transform duration-300">
                      <TemplateArt gradient={t.gradient} category={t.category} title={t.title} titleClass="text-2xl" />
                    </div>
                  )}
                  <span className="absolute top-2 left-2 nb-card px-2 py-0.5 text-xs font-bold bg-[#F9F871]">{t.category}</span>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <span className="font-display font-bold">{t.title}</span>
                  <span className="text-xs font-bold uppercase tracking-widest bg-[#A8E6CF] px-2 py-1 rounded-lg border-2 border-[#0A0A0A] opacity-0 group-hover:opacity-100 transition-opacity">
                    Customize
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && templates.length === 0 && (
          <div className="nb-card p-10 text-center mt-8">No templates in this category yet.</div>
        )}
      </div>
      <footer className="border-t-2 border-[#0A0A0A] py-8 text-center text-sm text-[#4A4A4A]">
        <div className="flex items-center justify-center flex-wrap gap-4 mb-3">
          <Link to="/terms" data-testid="footer-terms" className="font-semibold underline">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="font-semibold underline">Privacy Policy</Link>
          <Link to="/advertise" className="font-semibold underline">Advertise</Link>
          <ContactUs />
        </div>
        SendOccasions — made with <Heart className="w-4 h-4 inline" /> · Sending greetings is always free.
      </footer>
    </div>
  );
}
